import { useState, useEffect, useRef, useCallback } from "react";

const AnimatedSection = ({ images = [], captions = [] }) => {
  const [current, setCurrent] = useState(0);
  const [animate, setAnimate] = useState(false);
  const intervalRef = useRef(null);

  // Slide navigation
  const nextSlide = useCallback(() => {
    setCurrent((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevSlide = () => {
    setCurrent((prev) => (prev - 1 + images.length) % images.length);
  };

  // Auto slide every 30 seconds
  useEffect(() => {
    intervalRef.current = setInterval(nextSlide, 15000);
    return () => clearInterval(intervalRef.current);
  }, [nextSlide]);

  // Trigger animation on slide change
  useEffect(() => {
    setAnimate(true);
    const timeout = setTimeout(() => setAnimate(false), 1500); // match animation duration
    return () => clearTimeout(timeout);
  }, [current]);

  // Determine animation class based on index
  const getAnimationClass = (index) => {
    if (!animate) return "";
    switch (index) {
      case 0:
        return "animate-zoom";
      case 1:
        return "animate-slide-in-left";
      case 2:
        return "animate-slice"; // special slice animation
      case 3:
        return "animate-blur-in";
      default:
        return "";
    }
  };

  return (
    <div className="mt-[16px] w-full">
      <div className="items-center max-w-4xl mx-auto">
        {/* Image container */}
        <div className="relative w-full h-[350px] overflow-hidden border border-border rounded rounded shadow-lg bg-black">
          {current === 2 && animate ? (
            // Slice animation for slide 2
            <div className="grid grid-cols-10 w-full h-full">
              {[...Array(10)].map((_, idx) => (
                <div
                  key={idx}
                  className="overflow-hidden"
                  style={{
                    animation: `sliceAnim 0.6s ease ${idx * 0.05}s forwards`,
                  }}
                >
                  <img
                    src={images[current]}
                    alt={`Slice ${idx}`}
                    className="w-full h-[350px] object-cover"
                    style={{ transform: `translateX(-${idx * 10}%)` }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <img
              src={images[current]}
              alt={`Slide ${current + 1}`}
              className={`w-full h-[350px] object-cover ${getAnimationClass(current)}`}
            />
          )}

          {/* Navigation Buttons */}
          <button
            onClick={prevSlide}
            className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-white/70 hover:bg-white text-blue-600 font-bold rounded-full w-10 h-10 shadow"
          >
            ‹
          </button>
          <button
            onClick={nextSlide}
            className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-white/70 hover:bg-white text-blue-600 font-bold rounded-full w-10 h-10 shadow"
          >
            ›
          </button>
        </div>

        {/* Caption */}
        <div
          className={`text-2xl font-bold text-center text-secondary transition-all duration-700 ${
            animate ? "opacity-0 scale-90" : "opacity-100 scale-100"
          }`}
        >
          <span>{captions[current]}</span>
        </div>
      </div>

      {/* Keyframes for all animations */}
      <style>
        {`
          @keyframes sliceAnim {
            0% { transform: translateY(100%); opacity:0; }
            100% { transform: translateY(0); opacity:1; }
          }

          .animate-slice { animation: sliceAnim 0.7s ease; }

          @keyframes zoomIn {
            0% { transform: scale(1.2); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
          .animate-zoom { animation: zoomIn 0.7s ease; }

          @keyframes slideInLeft {
            0% { transform: translateX(-100%); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }
          .animate-slide-in-left { animation: slideInLeft 0.7s ease; }

          @keyframes blurIn {
            0% { filter: blur(10px); opacity: 0; }
            100% { filter: blur(0); opacity: 1; }
          }
          .animate-blur-in { animation: blurIn 0.7s ease; }
        `}
      </style>
    </div>
  );
};

export default AnimatedSection;
